import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import {
  TextInput,
  PasswordInput,
  Button,
  Paper,
  Title,
  Stack,
  Alert,
  Anchor,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { LoginFormSchema, type LoginFormData } from '../schemas/LoginForm.schema';
import { ApiError } from '@/shared/api/ApiError';
import { STORAGE_KEYS } from '@/lib/constants/storage';
import { AuthScreenLayout } from './AuthScreenLayout';

interface LoginSearch {
  redirect?: string;
}

export function LoginPage() {
  const { login, isAuthenticated, isUploadOnly } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as LoginSearch;
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Surface the reason for a forced sign-out (session expiry) once.
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEYS.SESSION_EXPIRED)) {
      sessionStorage.removeItem(STORAGE_KEYS.SESSION_EXPIRED);
      setNotice('Your session expired. Please sign in again.');
    }
  }, []);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    try {
      await login(data.username, data.password);
      // Redirect is handled reactively by the <Navigate> guard below, once the
      // auth state has committed — not imperatively here.
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Invalid username or password');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  // Drive the post-login redirect off committed auth state. Navigating imperatively
  // right after `await login()` raced React's state commit: ProtectedRoute could read
  // isAuthenticated=false on the same tick and bounce straight back to /login, so the
  // first Sign-in click appeared to do nothing and a second click was needed.
  if (isAuthenticated) {
    // Upload-only users land directly on their /upload home; a stale or
    // disallowed ?redirect is corrected by useAppRouteGuard after navigation.
    return <Navigate to={search.redirect ?? (isUploadOnly ? '/upload' : '/')} />;
  }

  return (
    <AuthScreenLayout>
      <Title ta="center" order={2}>
        Welcome back
      </Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Sign in to your account
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack>
            {notice && !error && (
              <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
                {notice}
              </Alert>
            )}
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error}
              </Alert>
            )}

            <TextInput
              label="Username"
              {...form.register('username')}
              error={form.formState.errors.username?.message}
            />

            <PasswordInput
              label="Password"
              placeholder="Your password"
              {...form.register('password')}
              error={form.formState.errors.password?.message}
            />

            <Button type="submit" fullWidth loading={form.formState.isSubmitting}>
              Sign in
            </Button>

            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="center"
              onClick={() => navigate({ to: '/forgot-password' })}
            >
              Forgot password?
            </Anchor>
          </Stack>
        </form>
      </Paper>
    </AuthScreenLayout>
  );
}
